import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Toast } from "primereact/toast";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCopy,
  FaSyncAlt,
  FaTimes,
  FaUsers,
  FaPercent,
  FaTags,
  FaBolt,
  FaUserFriends,
  FaSitemap,
  FaTh,
  FaTable,
  FaExclamationTriangle,
  FaCheck,
  FaCalculator,
  FaCrown,
  FaInbox,
  FaSearch,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes gpFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
@keyframes gpFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes gpSlide { from { transform: translateX(100%); } to { transform: none; } }
@keyframes gpZoom { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: none; } }
@keyframes gpBar { from { width: 0; } }
@keyframes gpFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
.gp-in { animation: gpFadeUp .55s cubic-bezier(.2,.7,.2,1) both; }
.gp-fade { animation: gpFade .2s ease both; }
.gp-slide { animation: gpSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.gp-zoom { animation: gpZoom .25s cubic-bezier(.2,.8,.2,1) both; }
.gp-bar { animation: gpBar .9s cubic-bezier(.2,.7,.2,1) both; }
.gp-float { animation: gpFloat 3s ease-in-out infinite; }
.gp-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.gp-press:hover { transform: translateY(-1px); }
.gp-press:active { transform: scale(.96); }
.gp-tier { transition: transform .3s cubic-bezier(.2,.8,.2,1), box-shadow .3s ease; }
.gp-tier:hover { transform: translateY(-6px); box-shadow: 0 26px 44px -22px rgba(15,23,42,.5); }
@media (prefers-reduced-motion: reduce) {
  .gp-in, .gp-fade, .gp-slide, .gp-zoom, .gp-bar, .gp-float { animation: none; }
  .gp-press:hover, .gp-press:active, .gp-tier:hover { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500";

// Colour for each tier, cheapest to most expensive
const TIER_STYLES = [
  { head: "from-slate-500 to-slate-700", ring: "ring-slate-300", accent: "bg-slate-600", soft: "bg-slate-100 text-slate-700" },
  { head: "from-amber-500 to-orange-600", ring: "ring-amber-300", accent: "bg-amber-500", soft: "bg-amber-100 text-amber-800" },
  { head: "from-cyan-500 to-blue-600", ring: "ring-cyan-300", accent: "bg-cyan-500", soft: "bg-cyan-100 text-cyan-800" },
  { head: "from-fuchsia-500 to-violet-700", ring: "ring-fuchsia-300", accent: "bg-fuchsia-500", soft: "bg-fuchsia-100 text-fuchsia-800" },
  { head: "from-emerald-500 to-teal-700", ring: "ring-emerald-300", accent: "bg-emerald-500", soft: "bg-emerald-100 text-emerald-800" },
  { head: "from-rose-500 to-red-700", ring: "ring-rose-300", accent: "bg-rose-500", soft: "bg-rose-100 text-rose-800" },
  { head: "from-indigo-600 to-slate-900", ring: "ring-indigo-300", accent: "bg-indigo-600", soft: "bg-indigo-100 text-indigo-800" },
];

const EMPTY = { pkg_id: "", pkg_name: "", price_start: "", price_end: "", direct_comission: "", team_comission: "", service_discount: "", daily_limit: "" };

/* ================= helpers ================= */

const num = (v) => Number(v || 0);
const money = (n) => `₹${num(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const priceText = (p) => (num(p.price_end) && num(p.price_end) !== num(p.price_start) ? `${money(p.price_start)} – ${money(p.price_end)}` : money(p.price_start));

const validate = (f) => {
  const e = {};
  if (!String(f.pkg_name).trim()) e.pkg_name = "Package name is required";
  if (f.price_start === "" || num(f.price_start) < 0) e.price_start = "Enter a price (0 or more)";
  if (f.price_end !== "" && num(f.price_end) < num(f.price_start)) e.price_end = "Must be equal to or more than the start price";
  ["direct_comission", "team_comission", "service_discount"].forEach((k) => {
    if (f[k] === "") e[k] = "Required";
    else if (num(f[k]) < 0 || num(f[k]) > 100) e[k] = "Must be between 0 and 100";
  });
  if (f.daily_limit === "" || num(f.daily_limit) < 0 || !Number.isInteger(num(f.daily_limit))) e.daily_limit = "Enter a whole number (0 or more)";
  return e;
};

/* ================= page ================= */

const GroupPackages = () => {
  const [packages, setPackages] = useState([]);
  const [usage, setUsage] = useState({}); // pkg_id -> member count
  const [usageLoaded, setUsageLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("tiers");
  const [query, setQuery] = useState("");

  const [editor, setEditor] = useState(null); // null | 'add' | 'edit'
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [calcPeople, setCalcPeople] = useState(10);

  const [confirm, setConfirm] = useState(null);
  const [working, setWorking] = useState(false);

  const toast = useRef(null);
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/group.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([post("getPackageList"), post("getGroupMembers").catch(() => null)]);
      setPackages(p?.status && Array.isArray(p.data) ? p.data : []);
      if (m && Array.isArray(m.members)) {
        const u = {};
        m.members.forEach((x) => {
          if (x.pkg_id) u[String(x.pkg_id)] = (u[String(x.pkg_id)] || 0) + 1;
        });
        setUsage(u);
        setUsageLoaded(true);
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load packages" });
    }
    setLoading(false);
  }, [post]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  /* ----- derived ----- */

  const sorted = useMemo(() => [...packages].sort((a, b) => num(a.price_start) - num(b.price_start)), [packages]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? sorted.filter((p) => String(p.pkg_name || "").toLowerCase().includes(q) || String(p.pkg_id).includes(q)) : sorted;
  }, [sorted, query]);

  const maxOf = useMemo(
    () => ({
      direct: Math.max(1, ...packages.map((p) => num(p.direct_comission))),
      team: Math.max(1, ...packages.map((p) => num(p.team_comission))),
      discount: Math.max(1, ...packages.map((p) => num(p.service_discount))),
      limit: Math.max(1, ...packages.map((p) => num(p.daily_limit))),
    }),
    [packages]
  );

  const popularId = useMemo(() => {
    let best = null;
    let max = 0;
    Object.entries(usage).forEach(([id, c]) => {
      if (c > max) {
        max = c;
        best = id;
      }
    });
    return best;
  }, [usage]);

  const totalMembers = Object.values(usage).reduce((s, c) => s + c, 0);

  const errors = validate(form);
  const hasErrors = Object.keys(errors).length > 0;
  const dupName = packages.some((p) => String(p.pkg_name || "").trim().toLowerCase() === String(form.pkg_name).trim().toLowerCase() && String(p.pkg_id) !== String(form.pkg_id));

  /* ----- editor ----- */

  const openAdd = (from) => {
    setForm(from ? { ...from, pkg_id: "", pkg_name: `${from.pkg_name} (copy)` } : EMPTY);
    setTouched({});
    setEditor("add");
  };
  const openEdit = (p) => {
    setForm({
      pkg_id: p.pkg_id,
      pkg_name: p.pkg_name ?? "",
      price_start: p.price_start ?? "",
      price_end: p.price_end ?? "",
      direct_comission: p.direct_comission ?? "",
      team_comission: p.team_comission ?? "",
      service_discount: p.service_discount ?? "",
      daily_limit: p.daily_limit ?? "",
    });
    setTouched({});
    setEditor("edit");
  };
  const closeEditor = () => !saving && setEditor(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setTouched({ pkg_name: 1, price_start: 1, price_end: 1, direct_comission: 1, team_comission: 1, service_discount: 1, daily_limit: 1 });
    if (hasErrors) {
      toast.current?.show({ severity: "error", summary: "Check the form", detail: Object.values(errors)[0] });
      return;
    }
    setSaving(true);
    try {
      const isEdit = editor === "edit";
      const payload = {
        pkg_name: String(form.pkg_name).trim(),
        price_start: num(form.price_start),
        price_end: form.price_end === "" ? num(form.price_start) : num(form.price_end),
        direct_comission: num(form.direct_comission),
        team_comission: num(form.team_comission),
        service_discount: num(form.service_discount),
        daily_limit: num(form.daily_limit),
      };
      if (isEdit) payload.pkg_id = form.pkg_id;
      const res = await post(isEdit ? "updatePackage" : "addPackage", payload);
      if (res?.error === 0 || res?.status === true) {
        toast.current?.show({ severity: "success", summary: "Saved", detail: res.message || "Package saved successfully" });
        setEditor(null);
        fetchPackages();
      } else {
        toast.current?.show({ severity: "error", summary: "Error", detail: res?.message || res?.error || "Failed to save package" });
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to save package" });
    }
    setSaving(false);
  };

  const runDelete = async () => {
    if (!confirm) return;
    setWorking(true);
    try {
      const res = await post("deletePackage", { pkg_id: confirm.pkg_id });
      if (res?.error === 0) {
        toast.current?.show({ severity: "success", summary: "Deleted", detail: res.message || "Package deleted successfully" });
        setConfirm(null);
        fetchPackages();
      } else {
        toast.current?.show({ severity: "error", summary: "Error", detail: res?.message || res?.error || "Failed to delete package" });
      }
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to delete package" });
    }
    setWorking(false);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (editor) {
        if (e.key === "Escape") closeEditor();
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  /* ----- pieces ----- */

  const perk = (icon, label, value, max, suffix, barClass) => {
    const Icon = icon;
    return (
      <li className="text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Icon size={11} className="text-slate-400" /> {label}
          </span>
          <b className="tabular-nums text-slate-900 dark:text-white">
            {value}
            {suffix}
          </b>
        </div>
        <div className="mt-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
          <div className={`gp-bar h-full rounded-full ${barClass}`} style={{ width: `${(num(value) / max) * 100}%` }} />
        </div>
      </li>
    );
  };

  const tierCard = (p, i) => {
    const S = TIER_STYLES[i % TIER_STYLES.length];
    const members = usage[String(p.pkg_id)] || 0;
    const popular = popularId && String(popularId) === String(p.pkg_id);
    const perRef = (num(p.price_start) * num(p.direct_comission)) / 100;
    return (
      <article key={p.pkg_id} className={`gp-tier gp-in relative rounded-3xl bg-white dark:bg-secondary-dark-bg border overflow-hidden flex flex-col ${popular ? `border-transparent ring-2 ${S.ring}` : "border-slate-200 dark:border-slate-700"}`} style={{ animationDelay: `${i * 80}ms` }}>
        <div className={`relative bg-gradient-to-br ${S.head} text-white px-5 pt-5 pb-8`}>
          <span className="pointer-events-none absolute -right-10 -top-10 w-36 h-36 rounded-full bg-white/15 blur-xl" />
          <div className="relative flex items-start justify-between gap-2">
            <span className="text-[11px] font-bold uppercase tracking-widest text-white/80">Level {i + 1}</span>
            {popular && (
              <span className="gp-float inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-white text-slate-900 shadow">
                <FaCrown size={9} className="text-amber-500" /> Most members
              </span>
            )}
          </div>
          <h3 className="relative mt-2 text-xl font-extrabold leading-tight break-words">{p.pkg_name}</h3>
          <p className="relative mt-3 text-4xl font-black tabular-nums tracking-tight">{priceText(p)}</p>
          <p className="relative text-xs text-white/80">one-time joining</p>
        </div>

        <div className="-mt-4 mx-4 relative rounded-2xl bg-white dark:bg-slate-800 shadow-md border border-slate-100 dark:border-slate-700 px-3 py-2 flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
            <FaUsers size={11} /> {usageLoaded ? `${members} member${members === 1 ? "" : "s"}` : "Members: -"}
          </span>
          <span className="text-slate-500">
            Earns <b className="text-emerald-600 tabular-nums">{money(perRef)}</b> per referral
          </span>
        </div>

        <ul className="p-5 space-y-3 flex-1">
          {perk(FaUserFriends, "Direct commission", num(p.direct_comission), maxOf.direct, "%", S.accent)}
          {perk(FaSitemap, "Team commission", num(p.team_comission), maxOf.team, "%", S.accent)}
          {perk(FaTags, "Service discount", num(p.service_discount), maxOf.discount, "%", S.accent)}
          {perk(FaBolt, "Daily limit", num(p.daily_limit), maxOf.limit, "/day", S.accent)}
        </ul>

        <div className="grid grid-cols-3 border-t border-slate-100 dark:border-slate-700">
          <button type="button" onClick={() => openEdit(p)} className="h-11 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 active:bg-slate-100 transition-colors">
            <FaEdit size={11} /> Edit
          </button>
          <button type="button" onClick={() => openAdd(p)} className="h-11 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 active:bg-slate-100 transition-colors border-x border-slate-100 dark:border-slate-700">
            <FaCopy size={11} /> Copy
          </button>
          <button type="button" onClick={() => setConfirm(p)} className="h-11 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 active:bg-rose-100 transition-colors">
            <FaTrash size={11} /> Delete
          </button>
        </div>
      </article>
    );
  };

  const field = (key, label, opts = {}) => {
    const err = touched[key] && errors[key];
    return (
      <div className={opts.full ? "sm:col-span-2" : ""}>
        <label htmlFor={`gp-${key}`} className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
          {label}
        </label>
        <div className="relative">
          {opts.prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{opts.prefix}</span>}
          <input
            id={`gp-${key}`}
            type={opts.type || "number"}
            inputMode={opts.type === "text" ? undefined : "decimal"}
            min={opts.type === "text" ? undefined : 0}
            max={opts.max}
            step={opts.step || "any"}
            value={form[key]}
            placeholder={opts.placeholder}
            onChange={(e) => setField(key, e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, [key]: 1 }))}
            className={`${INPUT} h-11 ${opts.prefix ? "pl-7" : "pl-3"} ${opts.suffix ? "pr-14" : "pr-3"} ${err ? "border-rose-300 focus:ring-rose-400" : ""}`}
          />
          {opts.suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{opts.suffix}</span>}
        </div>
        {err ? <p className="text-xs text-rose-600 mt-1">{err}</p> : opts.hint ? <p className="text-xs text-slate-400 mt-1">{opts.hint}</p> : null}
      </div>
    );
  };

  const previewIndex = editor === "edit" ? Math.max(0, sorted.findIndex((p) => String(p.pkg_id) === String(form.pkg_id))) : sorted.filter((p) => num(p.price_start) <= num(form.price_start)).length;
  const PS = TIER_STYLES[previewIndex % TIER_STYLES.length];
  const perRefForm = (num(form.price_start) * num(form.direct_comission)) / 100;
  const perTeamForm = (num(form.price_start) * num(form.team_comission)) / 100;

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="gp-in flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Group</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Packages</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Joining plans for groups. Price, commissions and daily limit for each level.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={12} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a package" className={`${INPUT} h-9 pl-8 w-48`} />
          </div>
          <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-700">
            {[
              { id: "tiers", icon: FaTh, label: "Cards" },
              { id: "compare", icon: FaTable, label: "Compare" },
            ].map((v) => {
              const Icon = v.icon;
              return (
                <button key={v.id} type="button" onClick={() => setView(v.id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-95 ${view === v.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                  <Icon size={11} /> {v.label}
                </button>
              );
            })}
          </div>
          <button type="button" onClick={fetchPackages} disabled={loading} className="gp-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button type="button" onClick={() => openAdd()} className="gp-press inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold shadow-sm">
            <FaPlus size={11} /> Add Package
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="gp-in grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" style={{ animationDelay: "60ms" }}>
        {[
          { label: "Packages", value: packages.length },
          { label: "Members on a package", value: usageLoaded ? totalMembers : "-" },
          { label: "Lowest price", value: sorted.length ? money(sorted[0].price_start) : "-" },
          { label: "Highest price", value: sorted.length ? money(sorted[sorted.length - 1].price_start) : "-" },
        ].map((s) => (
          <div key={s.label} className={`${CARD} px-4 py-3`}>
            <p className="text-xl font-bold text-slate-900 dark:text-white tabular-nums">{s.value}</p>
            <p className="text-xs text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Content */}
      {loading && !packages.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-96 rounded-3xl bg-slate-200 dark:bg-slate-700 animate-pulse" />
          ))}
        </div>
      ) : !shown.length ? (
        <div className={`${CARD} px-5 py-16 text-center`}>
          <FaInbox className="mx-auto text-slate-300" size={36} />
          <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">{packages.length ? "No package matches this search" : "No packages yet"}</p>
          {!packages.length && (
            <button type="button" onClick={() => openAdd()} className="gp-press mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold">
              <FaPlus size={11} /> Create the first package
            </button>
          )}
        </div>
      ) : view === "tiers" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 items-stretch">
          {shown.map((p) => tierCard(p, sorted.indexOf(p)))}
          <button type="button" onClick={() => openAdd()} className="gp-in gp-press rounded-3xl border-2 border-dashed border-slate-300 dark:border-slate-600 min-h-[320px] flex flex-col items-center justify-center gap-2 text-slate-500 hover:border-violet-400 hover:text-violet-600 hover:bg-violet-50/50 dark:hover:bg-violet-900/10">
            <span className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
              <FaPlus />
            </span>
            <span className="font-semibold">Add a new level</span>
          </button>
        </div>
      ) : (
        <div className={`${CARD} overflow-hidden gp-in`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-800 px-4 py-4 text-left font-medium text-slate-500 min-w-[170px]">Compare</th>
                  {shown.map((p) => {
                    const S = TIER_STYLES[sorted.indexOf(p) % TIER_STYLES.length];
                    return (
                      <th key={p.pkg_id} className="px-3 py-3 min-w-[150px] align-bottom">
                        <div className={`rounded-xl bg-gradient-to-br ${S.head} text-white px-3 py-2.5 text-left`}>
                          <p className="text-[10px] uppercase tracking-widest text-white/80">Level {sorted.indexOf(p) + 1}</p>
                          <p className="font-bold leading-tight">{p.pkg_name}</p>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {[
                  { label: "Price", render: (p) => <b className="text-lg">{priceText(p)}</b> },
                  { label: "Direct Commission", key: "direct_comission", max: maxOf.direct, suffix: "%" },
                  { label: "Team Commission", key: "team_comission", max: maxOf.team, suffix: "%" },
                  { label: "Service Discount", key: "service_discount", max: maxOf.discount, suffix: "%" },
                  { label: "Daily Limit", key: "daily_limit", max: maxOf.limit, suffix: "/day" },
                  { label: "Earns per referral", render: (p) => <span className="text-emerald-600 font-semibold">{money((num(p.price_start) * num(p.direct_comission)) / 100)}</span> },
                  { label: "Members", render: (p) => (usageLoaded ? <span className="inline-flex items-center gap-1.5"><FaUsers size={11} className="text-slate-400" /> {usage[String(p.pkg_id)] || 0}</span> : "-") },
                ].map((row) => (
                  <tr key={row.label} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
                    <td className="sticky left-0 z-10 bg-white dark:bg-secondary-dark-bg px-4 py-3 font-medium text-slate-600 dark:text-slate-300">{row.label}</td>
                    {shown.map((p) => {
                      const best = row.key && num(p[row.key]) === row.max && row.max > 0;
                      return (
                        <td key={p.pkg_id} className="px-3 py-3 tabular-nums text-slate-900 dark:text-white">
                          {row.render ? (
                            row.render(p)
                          ) : (
                            <div>
                              <span className={`inline-flex items-center gap-1 ${best ? "font-bold text-violet-700 dark:text-violet-300" : ""}`}>
                                {num(p[row.key])}
                                {row.suffix}
                                {best && <FaCheck size={9} />}
                              </span>
                              <div className="mt-1 h-1 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden max-w-[110px]">
                                <div className="gp-bar h-full rounded-full bg-violet-500" style={{ width: `${(num(p[row.key]) / row.max) * 100}%` }} />
                              </div>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr>
                  <td className="sticky left-0 z-10 bg-white dark:bg-secondary-dark-bg px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Action</td>
                  {shown.map((p) => (
                    <td key={p.pkg_id} className="px-3 py-3">
                      <div className="flex gap-1.5">
                        <button type="button" onClick={() => openEdit(p)} className="gp-press w-8 h-8 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center justify-center" aria-label="Edit">
                          <FaEdit size={11} />
                        </button>
                        <button type="button" onClick={() => openAdd(p)} className="gp-press w-8 h-8 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center justify-center" aria-label="Copy">
                          <FaCopy size={11} />
                        </button>
                        <button type="button" onClick={() => setConfirm(p)} className="gp-press w-8 h-8 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 flex items-center justify-center" aria-label="Delete">
                          <FaTrash size={11} />
                        </button>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================= Editor ================= */}
      {editor && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="gp-fade absolute inset-0 bg-slate-900/40" onClick={closeEditor} />
          <aside className="gp-slide relative w-full max-w-4xl h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-xs text-slate-500">{editor === "edit" ? `Package #${form.pkg_id}` : "New package"}</p>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{editor === "edit" ? "Edit Package" : "Add Package"}</h2>
              </div>
              <button type="button" onClick={closeEditor} className="gp-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Close">
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 p-5">
                <div className="lg:col-span-3 space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {field("pkg_name", "Package Name", { type: "text", full: true, placeholder: "e.g. YCNP GOLDEN 🥇", hint: "Emoji is allowed" })}
                    {dupName && <p className="sm:col-span-2 -mt-3 text-xs text-amber-600">Another package already has this name.</p>}
                    {field("price_start", "Price", { prefix: "₹", placeholder: "71" })}
                    {field("price_end", "Price up to (optional)", { prefix: "₹", placeholder: "Same as price", hint: "Leave empty for a fixed price" })}
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white mb-3 inline-flex items-center gap-2">
                      <FaPercent size={11} className="text-violet-500" /> Commission and benefits
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {field("direct_comission", "Direct Commission", { suffix: "%", max: 100, hint: "Paid to the member who referred" })}
                      {field("team_comission", "Team Commission", { suffix: "%", max: 100, hint: "Paid to the sub group creator" })}
                      {field("service_discount", "Service Discount", { suffix: "%", max: 100, hint: "Discount on YCNP services" })}
                      {field("daily_limit", "Daily Limit", { suffix: "/day", step: 1, hint: "Tasks allowed per day" })}
                    </div>
                    {num(form.direct_comission) + num(form.team_comission) > 100 && (
                      <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-xs p-3">
                        <FaExclamationTriangle className="mt-0.5 shrink-0" /> Direct + team commission is more than 100%. YCNP would pay out more than the joining price.
                      </p>
                    )}
                  </div>

                  {editor === "edit" && usageLoaded && (usage[String(form.pkg_id)] || 0) > 0 && (
                    <p className="flex items-start gap-2 rounded-xl bg-cyan-50 dark:bg-cyan-900/20 text-cyan-900 dark:text-cyan-100 text-xs p-3">
                      <FaUsers className="mt-0.5 shrink-0" /> {usage[String(form.pkg_id)]} members are on this package. Changes apply to them from now on.
                    </p>
                  )}
                </div>

                {/* Preview + calculator */}
                <div className="lg:col-span-2 space-y-4">
                  <div className="lg:sticky lg:top-0 space-y-4">
                    <div className="rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-lg">
                      <div className={`bg-gradient-to-br ${PS.head} text-white px-5 pt-5 pb-6`}>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-white/80">Preview</p>
                        <p className="mt-1 text-xl font-extrabold break-words">{String(form.pkg_name).trim() || "Package name"}</p>
                        <p className="mt-2 text-4xl font-black tabular-nums">{form.price_start === "" ? "₹0" : priceText(form)}</p>
                      </div>
                      <ul className="p-4 space-y-2 text-sm bg-white dark:bg-slate-800">
                        {[
                          ["Direct commission", `${num(form.direct_comission)}%`],
                          ["Team commission", `${num(form.team_comission)}%`],
                          ["Service discount", `${num(form.service_discount)}%`],
                          ["Daily limit", `${num(form.daily_limit)}/day`],
                        ].map(([k, v]) => (
                          <li key={k} className="flex justify-between">
                            <span className="text-slate-500">{k}</span>
                            <b className="tabular-nums text-slate-900 dark:text-white">{v}</b>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-4">
                      <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 inline-flex items-center gap-2">
                        <FaCalculator size={12} /> Earnings calculator
                      </p>
                      <label className="mt-3 block text-xs text-emerald-800 dark:text-emerald-200">
                        If a member brings <b>{calcPeople}</b> people on this package
                        <input type="range" min={1} max={100} value={calcPeople} onChange={(e) => setCalcPeople(Number(e.target.value))} className="w-full mt-1 accent-emerald-600" />
                      </label>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-center">
                        <div className="rounded-xl bg-white dark:bg-slate-800 p-2.5">
                          <p className="text-[10px] text-slate-500">Referrer earns</p>
                          <p className="font-bold text-emerald-700 tabular-nums">{money(perRefForm * calcPeople)}</p>
                          <p className="text-[10px] text-slate-400">{money(perRefForm)} each</p>
                        </div>
                        <div className="rounded-xl bg-white dark:bg-slate-800 p-2.5">
                          <p className="text-[10px] text-slate-500">Group creator earns</p>
                          <p className="font-bold text-emerald-700 tabular-nums">{money(perTeamForm * calcPeople)}</p>
                          <p className="text-[10px] text-slate-400">{money(perTeamForm)} each</p>
                        </div>
                      </div>
                      <p className="mt-2 text-[11px] text-emerald-800/80 dark:text-emerald-200/80">
                        YCNP keeps {money((num(form.price_start) - perRefForm - perTeamForm) * calcPeople)} from {money(num(form.price_start) * calcPeople)} collected.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <span className="hidden sm:inline text-xs text-slate-500">Ctrl + Enter to save</span>
              <div className="flex gap-2 ml-auto">
                <button type="button" onClick={closeEditor} disabled={saving} className="gp-press h-10 px-4 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
                  Cancel
                </button>
                <button type="button" onClick={save} disabled={saving} className="gp-press inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold disabled:opacity-60">
                  {saving ? <FaSyncAlt className="animate-spin" size={12} /> : <FaCheck size={11} />}
                  {saving ? "Saving..." : editor === "edit" ? "Save changes" : "Add package"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* ================= Delete confirm ================= */}
      {confirm && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="gp-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
          <div className="gp-zoom relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center">
              <FaTrash size={18} />
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">Delete "{confirm.pkg_name}"?</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Users will no longer see this package when joining a group. This cannot be undone.</p>
            {usageLoaded && (usage[String(confirm.pkg_id)] || 0) > 0 && (
              <p className="mt-3 flex items-start gap-2 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-rose-800 dark:text-rose-200 text-sm p-3">
                <FaExclamationTriangle className="mt-0.5 shrink-0" />
                <span>
                  <b>{usage[String(confirm.pkg_id)]} members</b> are on this package. Their commission and daily limit may stop working. Move them to another package first, or edit this one instead.
                </span>
              </p>
            )}
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="gp-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Keep it
              </button>
              <button type="button" onClick={runDelete} disabled={working} className="gp-press flex-1 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {working ? <FaSyncAlt className="animate-spin" size={12} /> : <FaTrash size={12} />}
                {working ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupPackages;